import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from './Modal';
import Spinner from './Spinner';
import { toast } from './Toast';
import { exportCSV, printTable } from './ExportButton';

const EMPTY = { furnizor:'', nrFactura:'', dataFactura:'', piesa:'', pret:'', bonConsum:'', dataBonConsum:'', nrInmatriculare:'', kilometri:'' };
const EMPTY_LINIE = { piesa:'', buc:'', pret:'' };
const emptyFactura = () => ({
  furnizor:'', nrFactura:'', dataFactura:'',
  dateInConsum: true,
  bonConsum:'', dataBonConsum:'', nrInmatriculare:'', kilometri:'',
  linii: [ {...EMPTY_LINIE}, {...EMPTY_LINIE}, {...EMPTY_LINIE} ]
});
const emptyBulkConsum = () => ({ bonConsum:'', dataBonConsum:'', nrInmatriculare:'', kilometri:'' });

function FilterInput({ value, onChange }) {
  return <input value={value} onChange={e=>onChange(e.target.value)} placeholder="Filtrează..."
    style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}/>;
}

function fmt(val) {
  const n = parseFloat(val||0);
  return n.toLocaleString('ro-RO', {minimumFractionDigits:2, maximumFractionDigits:2});
}

export default function PieseTab() {
  const isDriver = (localStorage.getItem('rol') || '') === 'conducator_auto';
  const [tab, setTab] = useState('all');
  const [rows, setRows] = useState([]);
  const [stoc, setStoc] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nruri, setNruri] = useState([]);
  const [filters, setFilters] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState({ col:'dataFactura', dir:'desc' });
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sugestiiPiese, setSugestiiPiese] = useState([]);
  const [furnizori, setFurnizori] = useState([]);

  const [stocDetail, setStocDetail] = useState(null);

  // Adaugă factură piese (mai multe linii dintr-o dată)
  const [facturaModal, setFacturaModal] = useState(false);
  const [facturaForm, setFacturaForm] = useState(emptyFactura());

  // Selecție + dare în consum în bloc, din Stoc magazie
  const [stocSelectati, setStocSelectati] = useState([]);
  const [bulkConsumModal, setBulkConsumModal] = useState(false);
  const [bulkConsumForm, setBulkConsumForm] = useState(emptyBulkConsum());

  const username = localStorage.getItem('username') || '';

  useEffect(() => { api.get('/api/auto/nruri').then(r=>setNruri(r.data.map(x=>x.nrInmatriculare))); }, []);
  useEffect(() => {
    reload();
    api.get('/api/piese/sugestii').then(r => setSugestiiPiese(r.data.map(x => x.piesa)));
    api.get('/api/piese/furnizori').then(r => setFurnizori(r.data.map(x => x.furnizor)));
  }, []);

  const reload = () => {
    setLoading(true);
    Promise.all([api.get('/api/piese'), api.get('/api/piese/stoc')])
      .then(([r1,r2])=>{ setRows(r1.data); setStoc(r2.data); setStocSelectati([]); })
      .finally(()=>setLoading(false));
  };

  const setFilter = (col,val) => setFilters(prev=>({...prev,[col]:val}));
  const toggleSort = (col) => setSort(prev=>({col,dir:prev.col===col&&prev.dir==='asc'?'desc':'asc'}));
  const sortIcon = (col) => sort.col===col?(sort.dir==='asc'?' ▲':' ▼'):' ⇅';

  const filtered = rows.filter(r=>{
    if (dateFrom && r.dataFactura && r.dataFactura < dateFrom) return false;
    if (dateTo && r.dataFactura && r.dataFactura > dateTo) return false;
    return Object.entries(filters).every(([col,val])=>!val||String(r[col]||'').toLowerCase().includes(val.toLowerCase()));
  });

  const sorted = [...filtered].sort((a,b)=>{
    const av=String(a[sort.col]||''), bv=String(b[sort.col]||'');
    return sort.dir==='asc'?av.localeCompare(bv):bv.localeCompare(av);
  });

  const totalPret = filtered.reduce((s,r)=>s+parseFloat(r.pret||0),0);
  const totalStoc = stoc.reduce((s,r)=>s+parseFloat(r.valoareTotala||0),0);

  const [campuriGoaleInitial, setCampuriGoaleInitial] = useState(new Set());

  const openEdit = (row) => {
    const goale = new Set();
    ['nrInmatriculare','kilometri','bonConsum','dataBonConsum'].forEach(k => {
      if (!String(row[k]||'').trim()) goale.add(k);
    });
    setCampuriGoaleInitial(goale);
    setForm({...row}); setModal({mode:'edit',id:row.nrCrtPiese});
  };

  const save = async () => {
    const hasAuto = !!String(form.nrInmatriculare||'').trim();
    const hasKm = !!String(form.kilometri||'').trim();
    if (hasAuto !== hasKm) {
      toast(hasAuto ? 'Ai completat Nr. Auto - completează și Kilometri.' : 'Ai completat Kilometri - completează și Nr. Auto.', 'error');
      return;
    }
    try {
      await api.put(`/api/piese/${modal.id}`, {...form, operare:username});
      setModal(null); reload();
      toast('Piesă actualizată!');
    } catch(e) { toast('Eroare: '+(e.response?.data?.error||e.message),'error'); }
  };

  const del = async (row) => {
    const mesaj = `Ștergi piesa "${row.piesa}" (factura ${row.nrFactura}, ${row.dataFactura}, ${fmt(row.pret)} RON) de la ${row.furnizor}?`;
    if (!window.confirm(mesaj)) return;
    try { await api.delete(`/api/piese/${row.nrCrtPiese}`); reload(); toast('Piesă ștearsă!'); }
    catch(e) { toast('Eroare la ștergere','error'); }
  };

  const REQUIRED_EDIT = new Set(['furnizor','nrFactura','dataFactura','piesa','pret']);
  const f = (field,label,type='text',opts=null) => (
    <div className="form-group"><label>{label}{REQUIRED_EDIT.has(field) && <span style={{color:'#e74c3c'}}> *</span>}</label>
      {opts
        ? <select value={form[field]||''} onChange={e=>setForm({...form,[field]:e.target.value})}>
            <option value="">--</option>{opts.map(o=><option key={o}>{o}</option>)}
          </select>
        : <input type={type} value={form[field]||''} onChange={e=>setForm({...form,[field]:e.target.value})}/>
      }
    </div>
  );

  const fEditable = (field, label, opts) => (
    <div className='form-group'><label>{label}{REQUIRED_EDIT.has(field) && <span style={{color:'#e74c3c'}}> *</span>}</label>
      <input list={`dl-${field}`} type='text' value={form[field]||''}
        onChange={e=>setForm({...form,[field]:e.target.value})}
        placeholder='Alege sau scrie...'/>
      <datalist id={`dl-${field}`}>{(opts||[]).map(o=><option key={o} value={o}/>)}</datalist>
    </div>
  );

  const SH = ({col,children}) => (
    <th onClick={()=>toggleSort(col)} style={{cursor:'pointer',userSelect:'none',whiteSpace:'nowrap'}}>
      {children}{sortIcon(col)}
    </th>
  );

  const openStocDetail = async (piesa) => {
    try {
      const r = await api.get(`/api/piese/stoc/${encodeURIComponent(piesa)}/randuri`);
      setStocDetail({ piesa, randuri: r.data });
    } catch(e) { toast('Eroare la încărcare', 'error'); }
  };

  const editFromStoc = (row) => {
    setStocDetail(null);
    openEdit(row);
  };

  const delFromStoc = async (row) => {
    const mesaj = `Ștergi din stoc piesa "${row.piesa}" (factura ${row.nrFactura}, ${row.dataFactura}, ${fmt(row.pret)} RON) de la ${row.furnizor}?`;
    if (!window.confirm(mesaj)) return;
    try {
      await api.delete(`/api/piese/${row.nrCrtPiese}`);
      toast('Piesă ștearsă din stoc!');
      setStocDetail(null);
      reload();
    } catch(e) { toast('Eroare la ștergere','error'); }
  };

  // ---- Adaugă factură piese (multi-linie) ----

  const openAddFactura = () => {
    setFacturaForm(nruri.length === 1 ? { ...emptyFactura(), nrInmatriculare: nruri[0] } : emptyFactura());
    setFacturaModal(true);
  };

  const updateLinie = (idx, field, val) => {
    setFacturaForm(prev => {
      const linii = prev.linii.slice();
      linii[idx] = { ...linii[idx], [field]: val };
      return { ...prev, linii };
    });
  };

  const addLinie = () => setFacturaForm(prev => ({ ...prev, linii: [...prev.linii, {...EMPTY_LINIE}] }));

  const removeLinie = (idx) => setFacturaForm(prev => {
    if (prev.linii.length <= 3) return prev;
    return { ...prev, linii: prev.linii.filter((_,i)=>i!==idx) };
  });

  const linieValoare = (l) => {
    const b = parseFloat(l.buc||0), p = parseFloat(l.pret||0);
    return (isNaN(b)||isNaN(p)) ? 0 : b*p;
  };
  const totalFactura = facturaForm.linii.reduce((s,l)=>s+linieValoare(l),0);

  const saveFactura = async () => {
    if (!String(facturaForm.furnizor||'').trim() || !String(facturaForm.nrFactura||'').trim() || !facturaForm.dataFactura) {
      toast('Completează Furnizor, Nr. Factură și Data facturii.', 'error'); return;
    }
    const liniiValide = facturaForm.linii.filter(l =>
      String(l.piesa||'').trim() && String(l.buc||'').trim() && String(l.pret||'').trim());
    if (liniiValide.length === 0) {
      toast('Adaugă cel puțin o piesă (denumire, buc și preț).', 'error'); return;
    }
    if (facturaForm.dateInConsum) {
      if (!String(facturaForm.bonConsum||'').trim() || !facturaForm.dataBonConsum ||
          !facturaForm.nrInmatriculare || !String(facturaForm.kilometri||'').trim()) {
        toast('Bon consum, Nr. Auto, Kilometri și Data bonului sunt obligatorii.', 'error'); return;
      }
    }
    try {
      await api.post('/api/piese/factura', {
        furnizor: facturaForm.furnizor, nrFactura: facturaForm.nrFactura, dataFactura: facturaForm.dataFactura,
        dateInConsum: facturaForm.dateInConsum,
        bonConsum: facturaForm.bonConsum, dataBonConsum: facturaForm.dataBonConsum,
        nrInmatriculare: facturaForm.nrInmatriculare, kilometri: facturaForm.kilometri,
        linii: liniiValide, operare: username
      });
      toast('Factură piese salvată!');
      setFacturaModal(false);
      reload();
    } catch(e) { toast('Eroare: '+(e.response?.data?.error||e.message),'error'); }
  };

  // ---- Selecție + dare în consum în bloc, din Stoc magazie ----

  const toggleStocSelectat = (piesa) => setStocSelectati(prev =>
    prev.includes(piesa) ? prev.filter(p=>p!==piesa) : [...prev, piesa]);

  const toggleStocSelectatiToti = () => setStocSelectati(prev =>
    prev.length === stoc.length ? [] : stoc.map(r=>r.piesa));

  const openBulkConsum = () => { setBulkConsumForm(emptyBulkConsum()); setBulkConsumModal(true); };

  const stocRandSelectate = stoc.filter(r=>stocSelectati.includes(r.piesa));
  const totalBulkConsum = stocRandSelectate.reduce((s,r)=>s+parseFloat(r.valoareTotala||0),0);

  const saveBulkConsum = async () => {
    if (!String(bulkConsumForm.bonConsum||'').trim() || !bulkConsumForm.dataBonConsum ||
        !bulkConsumForm.nrInmatriculare || !String(bulkConsumForm.kilometri||'').trim()) {
      toast('Completează toate câmpurile', 'error'); return;
    }
    try {
      await api.post('/api/piese/dare-consum-bulk', {
        piese: stocSelectati,
        bonConsum: bulkConsumForm.bonConsum,
        dataBonConsum: bulkConsumForm.dataBonConsum,
        nrInmatriculare: bulkConsumForm.nrInmatriculare,
        kilometri: bulkConsumForm.kilometri
      });
      toast('Piese date în consum!');
      setBulkConsumModal(false);
      setStocSelectati([]);
      reload();
    } catch(e) { toast('Eroare: '+(e.response?.data?.error||e.message),'error'); }
  };

  return (
    <div>
      <div className="tabs">
        <button className={`tab-btn ${tab==='all'?'active':''}`} onClick={()=>setTab('all')}>Piese consumate</button>
        <button className={`tab-btn ${tab==='stoc'?'active':''}`} onClick={()=>setTab('stoc')}>Stoc magazie</button>
      </div>

      {tab==='all' && <>
        <div className="date-filter-bar">
          <label>Perioadă:</label>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}/>
          <span className="sep">—</span>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}/>
          {(dateFrom||dateTo) && <button className="btn btn-sm" style={{background:'#eee'}} onClick={()=>{setDateFrom('');setDateTo('');}}>✕</button>}
        </div>

        <div style={{background:'#e8eef5',padding:'8px 14px',borderBottom:'1px solid #d0dcea',display:'flex',gap:16,alignItems:'center',flexWrap:'wrap'}}>
          <span style={{fontSize:12,color:'#555'}}>{filtered.length} înregistrări{Object.values(filters).some(v=>v)||dateFrom||dateTo?' (filtrate)':''}</span>
          <span style={{fontWeight:'bold',color:'#1a3a5c',fontSize:13}}>Total valoare: {fmt(totalPret)} RON</span>
          <div style={{marginLeft:'auto',display:'flex',gap:6}}>
            <button className="btn btn-sm" style={{background:'#217346',color:'white'}} onClick={()=>exportCSV(sorted,'piese')}>📊 CSV</button>
            <button className="btn btn-sm" style={{background:'#555',color:'white'}} onClick={()=>printTable('Piese consumate',sorted)}>🖨️ Print</button>
            {!isDriver && <button className="btn btn-success btn-sm" onClick={openAddFactura}>+ Adaugă factură piese</button>}
          </div>
        </div>

        {loading?<Spinner/>:(
          <div style={{overflowX:'auto'}}>
            <table>
              <thead>
                <tr>
                  <SH col="furnizor">Furnizor</SH><SH col="nrFactura">Nr. Factură</SH>
                  <SH col="dataFactura">Data</SH><SH col="piesa">Piesă</SH>
                  <SH col="pret">Preț (RON)</SH><SH col="nrInmatriculare">Nr. Auto</SH>
                  <SH col="kilometri">Kilometri</SH>
                  <SH col="bonConsum">Bon consum</SH><th>Data bon</th><th>Acțiuni</th>
                </tr>
                <tr style={{background:'#f0f4fa'}}>
                  <th><FilterInput value={filters.furnizor||''} onChange={v=>setFilter('furnizor',v)}/></th>
                  <th><FilterInput value={filters.nrFactura||''} onChange={v=>setFilter('nrFactura',v)}/></th>
                  <th></th>
                  <th><FilterInput value={filters.piesa||''} onChange={v=>setFilter('piesa',v)}/></th>
                  <th></th>
                  <th>
                    <select value={filters.nrInmatriculare||''} onChange={e=>setFilter('nrInmatriculare',e.target.value)}
                      style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}>
                      <option value="">Toate</option>
                      {nruri.map(n=><option key={n} value={n}>{n}</option>)}
                    </select>
                  </th>
                  <th></th>
                  <th><FilterInput value={filters.bonConsum||''} onChange={v=>setFilter('bonConsum',v)}/></th>
                  <th></th><th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(r=>(
                  <tr key={r.nrCrtPiese}>
                    <td>{r.furnizor}</td><td>{r.nrFactura}</td><td>{r.dataFactura}</td>
                    <td><b>{r.piesa}</b></td>
                    <td style={{textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(r.pret)}</td>
                    <td>{r.nrInmatriculare}</td>
                    <td style={{textAlign:'right'}}>{r.kilometri ? Number(r.kilometri).toLocaleString('ro-RO') : ''}</td>
                    <td>{r.bonConsum}</td><td>{r.dataBonConsum}</td>
                    <td style={{whiteSpace:'nowrap'}}>
                      {!isDriver && (<>
                        <button className="btn btn-sm" style={{marginRight:4}} onClick={()=>openEdit(r)}>✏️</button>
                        <button className="btn btn-sm btn-danger" onClick={()=>del(r)}>🗑️</button>
                      </>)}
                    </td>
                  </tr>
                ))}
                {sorted.length===0&&<tr><td colSpan={10} style={{textAlign:'center',padding:20,color:'#888'}}>Fără date</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </>}

      {tab==='stoc' && (
        <div style={{padding:16}}>
          <div style={{marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
            <span style={{fontWeight:'bold',color:'#1a3a5c'}}>
              Total valoare stoc: <span style={{color:'#27ae60'}}>{fmt(totalStoc)} RON</span>
            </span>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              {!isDriver && stocSelectati.length>0 && (
                <button className="btn btn-sm btn-success" onClick={openBulkConsum}>
                  🔧 Dați în consum ({stocSelectati.length})
                </button>
              )}
              <button className="btn btn-sm" style={{background:'#217346',color:'white'}} onClick={()=>exportCSV(stoc,'stoc_piese')}>📊 CSV</button>
              <button className="btn btn-sm" style={{background:'#555',color:'white'}} onClick={()=>printTable('Stoc magazie',stoc)}>🖨️ Print</button>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                {!isDriver && (
                  <th style={{width:30}}>
                    <input type="checkbox" checked={stoc.length>0 && stocSelectati.length===stoc.length}
                      onChange={toggleStocSelectatiToti} title="Selectează tot"/>
                  </th>
                )}
                <th>Piesă</th><th>Cantitate</th><th style={{textAlign:'right'}}>Valoare totală (RON)</th><th>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {stoc.map(r=>(
                <tr key={r.piesa}>
                  {!isDriver && (
                    <td style={{textAlign:'center'}}>
                      <input type="checkbox" checked={stocSelectati.includes(r.piesa)}
                        onChange={()=>toggleStocSelectat(r.piesa)}/>
                    </td>
                  )}
                  <td><b>{r.piesa}</b></td><td>{r.cantitate}</td>
                  <td style={{textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(r.valoareTotala)}</td>
                  <td style={{whiteSpace:'nowrap'}}>
                    <button className="btn btn-sm" style={{marginRight:4}} onClick={()=>openStocDetail(r.piesa)} title="Modifică / Șterge">📋 Detalii</button>
                  </td>
                </tr>
              ))}
              {stoc.length===0&&<tr><td colSpan={isDriver?4:5} style={{textAlign:'center',padding:20,color:'#888'}}>Stoc gol</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title="Editează piesă" onClose={()=>setModal(null)} onSave={save}>
          <div className="form-row">{f('furnizor','Furnizor')}{f('nrFactura','Nr. Factură')}</div>
          <div className="form-row">{f('dataFactura','Data factură','date')}{fEditable('piesa','Denumire piesă',sugestiiPiese)}</div>
          {campuriGoaleInitial.has('nrInmatriculare')
            ? <div className="form-row single">{f('pret','Preț (RON)','number')}</div>
            : <div className="form-row">{f('pret','Preț (RON)','number')}{f('nrInmatriculare','Nr. Auto','text',nruri)}</div>}
          {(!campuriGoaleInitial.has('kilometri') || !campuriGoaleInitial.has('bonConsum')) && (
            <div className="form-row">
              {!campuriGoaleInitial.has('kilometri') && f('kilometri','Kilometri','number')}
              {!campuriGoaleInitial.has('bonConsum') && f('bonConsum','Bon consum')}
            </div>
          )}
          {!campuriGoaleInitial.has('dataBonConsum') &&
            <div className="form-row single">{f('dataBonConsum','Data bon consum','date')}</div>}
          <div className="form-row single"><span style={{fontSize:11,color:'#888'}}>* Câmp obligatoriu</span></div>
        </Modal>
      )}

      {stocDetail && (
        <Modal title={`Stoc — ${stocDetail.piesa}`} onClose={()=>setStocDetail(null)}>
          <table>
            <thead><tr><th>Furnizor</th><th>Nr. Factură</th><th>Data</th><th style={{textAlign:'right'}}>Preț</th><th>Acțiuni</th></tr></thead>
            <tbody>
              {stocDetail.randuri.map(row=>(
                <tr key={row.nrCrtPiese}>
                  <td>{row.furnizor}</td><td>{row.nrFactura}</td><td>{row.dataFactura}</td>
                  <td style={{textAlign:'right'}}>{fmt(row.pret)}</td>
                  <td style={{whiteSpace:'nowrap'}}>
                    <button className="btn btn-sm" style={{marginRight:4}} onClick={()=>editFromStoc(row)}>✏️</button>
                    <button className="btn btn-sm btn-danger" onClick={()=>delFromStoc(row)}>🗑️</button>
                  </td>
                </tr>
              ))}
              {stocDetail.randuri.length===0&&<tr><td colSpan={5} style={{textAlign:'center',padding:20,color:'#888'}}>Niciun rând</td></tr>}
            </tbody>
          </table>
        </Modal>
      )}

      {facturaModal && (
        <Modal title="Adaugă factură piese" onClose={()=>setFacturaModal(false)} onSave={saveFactura}>
          <div className="form-row">
            <div className="form-group"><label>Furnizor<span style={{color:'#e74c3c'}}> *</span></label>
              <input list="dl-furnizor-factura" type="text" value={facturaForm.furnizor}
                onChange={e=>setFacturaForm({...facturaForm, furnizor:e.target.value})}
                placeholder="Alege sau scrie..."/>
              <datalist id="dl-furnizor-factura">{furnizori.map(fz=><option key={fz} value={fz}/>)}</datalist>
            </div>
            <div className="form-group"><label>Nr. Factură<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="text" value={facturaForm.nrFactura} onChange={e=>setFacturaForm({...facturaForm,nrFactura:e.target.value})}/>
            </div>
          </div>
          <div className="form-row single">
            <div className="form-group"><label>Data factură<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="date" value={facturaForm.dataFactura} onChange={e=>setFacturaForm({...facturaForm,dataFactura:e.target.value})}/>
            </div>
          </div>

          <div style={{
            background: facturaForm.dateInConsum ? '#e8f8ee' : '#f7f9fc',
            border: '2px solid ' + (facturaForm.dateInConsum ? '#27ae60' : '#ccc'),
            borderRadius:6, padding:'10px 14px', margin:'10px 0 14px',
            display:'flex', alignItems:'center', gap:10
          }}>
            <input type="checkbox" id="dateInConsum" checked={facturaForm.dateInConsum}
              onChange={e=>setFacturaForm({...facturaForm, dateInConsum:e.target.checked})}
              style={{width:18,height:18,cursor:'pointer'}}/>
            <label htmlFor="dateInConsum" style={{fontWeight:'bold', fontSize:13, cursor:'pointer', color: facturaForm.dateInConsum?'#1a7a3a':'#555'}}>
              🔧 Date în consum — piesele se montează direct pe un vehicul
            </label>
          </div>

          {facturaForm.dateInConsum && (
            <>
              <div className="form-row">
                <div className="form-group"><label>Bon consum nr.<span style={{color:'#e74c3c'}}> *</span></label>
                  <input type="text" value={facturaForm.bonConsum} onChange={e=>setFacturaForm({...facturaForm,bonConsum:e.target.value})}/>
                </div>
                <div className="form-group"><label>Nr. Auto<span style={{color:'#e74c3c'}}> *</span></label>
                  <select value={facturaForm.nrInmatriculare} onChange={e=>setFacturaForm({...facturaForm,nrInmatriculare:e.target.value})}>
                    <option value="">--</option>
                    {nruri.map(n=><option key={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Kilometri<span style={{color:'#e74c3c'}}> *</span></label>
                  <input type="number" value={facturaForm.kilometri} onChange={e=>setFacturaForm({...facturaForm,kilometri:e.target.value})}/>
                </div>
                <div className="form-group"><label>Data bon consum<span style={{color:'#e74c3c'}}> *</span></label>
                  <input type="date" value={facturaForm.dataBonConsum} onChange={e=>setFacturaForm({...facturaForm,dataBonConsum:e.target.value})}/>
                </div>
              </div>
            </>
          )}
          {!facturaForm.dateInConsum && (
            <div style={{fontSize:12, color:'#888', marginBottom:10}}>
              Piesele adăugate mai jos vor intra în Stoc magazie.
            </div>
          )}

          <hr style={{border:'none',borderTop:'1px solid #ddd', margin:'6px 0 14px'}}/>

          <table style={{width:'100%'}}>
            <thead>
              <tr>
                <th style={{textAlign:'left'}}>Denumire piesă<span style={{color:'#e74c3c'}}> *</span></th>
                <th style={{width:70}}>Buc<span style={{color:'#e74c3c'}}> *</span></th>
                <th style={{width:100}}>Preț<span style={{color:'#e74c3c'}}> *</span></th>
                <th style={{width:110,textAlign:'right'}}>Valoare</th>
                <th style={{width:28}}></th>
              </tr>
            </thead>
            <tbody>
              {facturaForm.linii.map((l,idx)=>(
                <tr key={idx}>
                  <td>
                    <input list={`dl-linie-piesa-${idx}`} type="text" value={l.piesa}
                      onChange={e=>updateLinie(idx,'piesa',e.target.value)}
                      placeholder="Alege sau scrie..." style={{width:'100%'}}/>
                    <datalist id={`dl-linie-piesa-${idx}`}>{sugestiiPiese.map(o=><option key={o} value={o}/>)}</datalist>
                  </td>
                  <td><input type="number" min="0" value={l.buc} onChange={e=>updateLinie(idx,'buc',e.target.value)} style={{width:'100%'}}/></td>
                  <td><input type="number" min="0" step="0.01" value={l.pret} onChange={e=>updateLinie(idx,'pret',e.target.value)} style={{width:'100%'}}/></td>
                  <td style={{textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(linieValoare(l))}</td>
                  <td style={{textAlign:'center'}}>
                    {facturaForm.linii.length>3 &&
                      <button type="button" className="btn btn-sm btn-danger" onClick={()=>removeLinie(idx)} title="Șterge linia">✕</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{marginTop:6}}>
            <a href="#" onClick={e=>{e.preventDefault(); addLinie();}} style={{fontSize:13,color:'#2a5a8c'}}>+ Adaugă linie</a>
          </div>
          <div style={{textAlign:'right', fontWeight:'bold', color:'#1a3a5c', marginTop:10, fontSize:14}}>
            Total factură: {fmt(totalFactura)} RON
          </div>
          <div className="form-row single"><span style={{fontSize:11,color:'#888'}}>* Câmp obligatoriu {facturaForm.dateInConsum && '(inclusiv datele de consum, dacă bifa e activă)'}</span></div>
        </Modal>
      )}

      {bulkConsumModal && (
        <Modal title="Dați în consum" onClose={()=>setBulkConsumModal(false)} onSave={saveBulkConsum}>
          <table style={{width:'100%', marginBottom:10}}>
            <thead><tr><th style={{textAlign:'left'}}>Piesă</th><th>Cantitate</th><th style={{textAlign:'right'}}>Valoare (RON)</th></tr></thead>
            <tbody>
              {stocRandSelectate.map(r=>(
                <tr key={r.piesa}>
                  <td><b>{r.piesa}</b></td><td style={{textAlign:'center'}}>{r.cantitate}</td>
                  <td style={{textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(r.valoareTotala)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{textAlign:'right', fontWeight:'bold', color:'#1a3a5c', marginBottom:14}}>
            Total: {fmt(totalBulkConsum)} RON
          </div>
          <div className="form-row">
            <div className="form-group"><label>Nr. Bon consum<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="text" value={bulkConsumForm.bonConsum} onChange={e=>setBulkConsumForm({...bulkConsumForm,bonConsum:e.target.value})}/>
            </div>
            <div className="form-group"><label>Data<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="date" value={bulkConsumForm.dataBonConsum} onChange={e=>setBulkConsumForm({...bulkConsumForm,dataBonConsum:e.target.value})}/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Nr. Auto<span style={{color:'#e74c3c'}}> *</span></label>
              <select value={bulkConsumForm.nrInmatriculare} onChange={e=>setBulkConsumForm({...bulkConsumForm,nrInmatriculare:e.target.value})}>
                <option value="">--</option>
                {nruri.map(n=><option key={n}>{n}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Kilometri<span style={{color:'#e74c3c'}}> *</span></label>
              <input type="number" value={bulkConsumForm.kilometri} onChange={e=>setBulkConsumForm({...bulkConsumForm,kilometri:e.target.value})}/>
            </div>
          </div>
          <div className="form-row single"><span style={{fontSize:11,color:'#888'}}>* Câmp obligatoriu</span></div>
        </Modal>
      )}
    </div>
  );
}
